'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import {
  ArrowUpRight,
  ChevronDown,
  ExternalLink,
  EyeOff,
  Heart,
  Info,
  MapPin,
  Megaphone,
} from 'lucide-react'
import { cn, formatPrice, proxyImageUrl } from '@/lib/utils'
import {
  useSearchStore,
  useSettingsStore,
  useUIStore,
  useUserStore,
} from '@/lib/store'
import { useSaveListing, MAX_LIKED_LISTINGS } from './use-save-listing'
import {
  Chip,
  PricePositionBar,
  RealPhotos,
  ScrimBtn,
  StatRow,
  isLikelyMonthlyPrice,
  paletteFor,
} from '@/components/roostr'
import { AiSummaryButton } from '@/components/listing-detail'
import { useListingAmenities, type AmenityItem } from '@/lib/use-listing-amenities'
import { useDeadListingDetection } from '@/lib/use-dead-listing-detection'
import { api } from '@/lib/api'
import type { Listing } from '@/lib/types'
import { useI18n } from '@/lib/i18n'

// Page-level pools computed by the grid from the currently visible results.
// The PricePositionBar consumes them; with no stats (map popup, liked page)
// the position bar simply renders nothing.
export interface ListingCardStats {
  prices: number[]
  areas: number[]
  floors: number[]
  priceMin: number
  priceMax: number
  priceMedian: number
}

interface ListingCardProps {
  listing: Listing
  className?: string
  variant?: 'default' | 'map'
  stats?: ListingCardStats
  /**
   * Keyboard browsing (lib/use-grid-keyboard-nav.ts). The grid runs a roving
   * tabindex — exactly one card is 0, so the whole grid costs one tab stop —
   * and drives the ring off `focused` rather than :focus-visible, which is not
   * guaranteed to match a programmatic focus().
   */
  tabIndex?: number
  focused?: boolean
}

// LIKE button on the photo scrim — the design's ScrimBtn visual (cards.jsx:291)
// wrapping the shared useSaveListing hook (auth gating, optimistic updates,
// planner sync, once-per-uid saved-ids sync, per-listing op serialization).
function SaveScrimBtn({ listingId }: { listingId: string }) {
  const { t } = useI18n()
  const { isSaved, isSaving, toggleSave, atLimit } = useSaveListing(listingId)

  // At the cap, an unsaved listing's heart reads as "full" (dimmed) — clicking
  // still surfaces the limit toast via toggleSave rather than saving.
  return (
    <ScrimBtn
      icon={Heart}
      active={isSaved}
      title={
        isSaved
          ? t('listingCard.unlike', 'Remove from liked')
          : atLimit
            ? t('liked.fullTip', 'Liked list full ({n}) — remove one to add another').replace(
                '{n}',
                String(MAX_LIKED_LISTINGS)
              )
            : t('listingCard.like', 'Like')
      }
      onClick={toggleSave}
      style={{ background: 'rgba(26, 23, 23, 0.35)', opacity: isSaving ? 0.6 : atLimit ? 0.55 : undefined }}
    />
  )
}

// Effective monthly price — base rent plus recurring extras (utilities,
// parking, internet, service charges) unless the use_total_price setting is
// explicitly off. Exported so the grid's stats pool uses the SAME operand as
// the number each card displays (PricePositionBar consistency).
export function effectiveListingPrice(
  listing: Pick<Listing, 'price' | 'price_breakdown'>,
  useTotalPrice?: boolean
): number {
  const pb = listing.price_breakdown
  if (useTotalPrice === false || !pb) return listing.price
  return (
    listing.price +
    (pb.utilities_monthly || 0) +
    (pb.parking_monthly || 0) +
    (pb.internet_monthly || 0) +
    (pb.service_charges_monthly || 0)
  )
}

// Breakdown rows from the real price_breakdown — rows with no data are
// omitted, values are never invented. The recurring rows (rent + utilities +
// service charges + parking + internet) sum to effectiveListingPrice; deposit
// is one-time and listed last. Exported so the What's-new inbox rows show the
// SAME breakdown the search card does.
export function cardPriceRows(
  listing: Pick<Listing, 'price' | 'price_currency' | 'price_breakdown'>,
  t: (key: string, fallback?: string) => string
): Array<[string, string]> {
  const pb = listing.price_breakdown
  const rows: Array<[string, string]> = [
    [t('listingCard.monthlyRent', 'Monthly rent'), formatPrice(listing.price, listing.price_currency)],
  ]
  if (pb?.utilities_monthly) {
    rows.push([t('listingCard.utilities', 'Utilities'), formatPrice(pb.utilities_monthly, listing.price_currency)])
  }
  if (pb?.service_charges_monthly) {
    rows.push([
      t('listingCard.serviceCharges', 'Service charges'),
      formatPrice(pb.service_charges_monthly, listing.price_currency),
    ])
  }
  if (pb?.parking_monthly) {
    rows.push([t('listingCard.parking', 'Parking'), formatPrice(pb.parking_monthly, listing.price_currency)])
  }
  if (pb?.internet_monthly) {
    rows.push([t('listingCard.internet', 'Internet'), formatPrice(pb.internet_monthly, listing.price_currency)])
  }
  if (pb?.deposit_amount) {
    rows.push([t('listingCard.deposit', 'Deposit'), formatPrice(pb.deposit_amount, listing.price_currency)])
  } else if (pb?.deposit_months) {
    rows.push([
      t('listingCard.deposit', 'Deposit'),
      `${pb.deposit_months} ${pb.deposit_months > 1 ? t('listingCard.monthMany', 'months') : t('listingCard.monthOne', 'month')}`,
    ])
  }
  return rows
}

// Price block — cards.jsx:201-242. The monthly price is always visible; the
// breakdown appears as an absolute overlay popover above the row (hover or
// sticky click toggle) so the card layout never shifts.
function CardPriceBlock({
  listing,
  displayPrice,
  stats,
  isCompact,
}: {
  listing: Listing
  displayPrice: number
  stats?: ListingCardStats
  isCompact: boolean
}) {
  const { t } = useI18n()
  const [open, setOpen] = React.useState(false)

  const rows = cardPriceRows(listing, t)
  const hasBreakdown = rows.length > 1

  const priceStats =
    stats && stats.prices.length >= 2
      ? { min: stats.priceMin, max: stats.priceMax, median: stats.priceMedian }
      : undefined

  return (
    // Full width so the position bar and the breakdown popover
    // (absolute inset-x-0) still span the card; the interactive price row
    // inside is content-width.
    <div className="relative pb-[2px] pt-2">
      {/* w-fit + the chevron directly after the (i): the toggle is the price
          itself, so it ends with its icons instead of stretching the whole
          card width (justify-between used to fling the chevron to the far
          edge and made the entire row hoverable/clickable). */}
      <div
        onMouseEnter={hasBreakdown ? () => setOpen(true) : undefined}
        onMouseLeave={hasBreakdown ? () => setOpen(false) : undefined}
        onClick={
          hasBreakdown
            ? (e) => {
                e.stopPropagation()
                setOpen((p) => !p)
              }
            : undefined
        }
        title={open || !hasBreakdown ? undefined : t('listingCard.costBreakdown', 'Cost breakdown')}
        className={cn('flex w-fit max-w-full items-center gap-2', hasBreakdown && 'cursor-pointer')}
      >
        <div className="flex items-baseline gap-[5px]">
          <span
            className={cn(
              'font-mono font-bold',
              displayPrice > 0 ? 'text-primary' : 'text-ink-muted',
              isCompact ? 'text-[18px]' : 'text-[20px]'
            )}
          >
            {displayPrice > 0
              ? formatPrice(displayPrice, listing.price_currency)
              : t('listingCard.noPrice', 'No price')}
          </span>
          {displayPrice > 0 && isLikelyMonthlyPrice(displayPrice) && (
            <span className="text-[12.5px] text-ink-muted">{t('listingCard.perMo', '/mo')}</span>
          )}
          {hasBreakdown && <Info size={13} className="shrink-0 self-center text-ink-muted" />}
        </div>
        {hasBreakdown && (
          <ChevronDown
            size={18}
            className={cn(
              'shrink-0 text-ink-muted transition-transform duration-[250ms] ease-spatial',
              open && 'rotate-180'
            )}
          />
        )}
      </div>
      {/* Same operand as the displayed number — the grid's stats pool is
          built from effectiveListingPrice too, so dot and range agree. */}
      {displayPrice > 0 && <PricePositionBar listing={{ price: displayPrice }} stats={priceStats} />}
      {open && hasBreakdown && (
        <div className="absolute inset-x-0 bottom-[calc(100%+6px)] z-[55] animate-pop-in rounded-shape-sm border border-outline bg-surface px-[13px] py-[5px] shadow-e3">
          {rows.map(([k, v], i) => (
            <div
              key={k}
              className={cn(
                'flex items-center justify-between py-[7px]',
                i < rows.length - 1 && 'border-b border-outline-variant'
              )}
            >
              <span className="text-[12.5px] text-ink-secondary">{k}</span>
              <span className="font-mono text-[12.5px] font-semibold">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// M3 Expressive "breakdown" listing card — .agent/handoff-web/cards.jsx:259-392.
// Memoized to prevent unnecessary re-renders when the parent re-renders.
export const ListingCard = React.memo(function ListingCard({
  listing,
  className,
  variant = 'default',
  stats,
  tabIndex = -1,
  focused = false,
}: ListingCardProps) {
  const { t } = useI18n()
  const { hideListing, hiddenListings, highlightedKeywords, activeTags, addTag, filters } = useSearchStore()
  const { openGallery } = useUIStore()
  const { savedListingIds } = useUserStore()
  const { cardSize, dealbreakers } = useSettingsStore()
  // Settings must-haves are applied to every search (merged into the request)
  // but never enter activeTags, so the card needs them to label their chips.
  const mustHaveKeys = React.useMemo(
    () => new Set(dealbreakers?.mustHave || []),
    [dealbreakers?.mustHave]
  )
  const router = useRouter()
  const isCompact = cardSize < 280

  const isSaved = savedListingIds.includes(listing.id)
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0)
  const [tagsOpen, setTagsOpen] = React.useState(false)
  const tagsRef = React.useRef<HTMLDivElement>(null)
  const photoRef = React.useRef<HTMLDivElement>(null)
  const [sawImageError, setSawImageError] = React.useState(false)

  const isHidden = hiddenListings.has(listing.id)

  const imageUrls = React.useMemo(
    () => listing.images.map((img) => proxyImageUrl(img.url)),
    [listing.images],
  )

  // Deterministic palette for the gradient fallback (RealPhotos error state).
  const paletteIndex = paletteFor(listing.id)

  // RealPhotos swaps broken images for its gradient fallback internally; error
  // events don't bubble, so listen in the capture phase to learn a photo broke
  // and arm production's dead-listing probe.
  React.useEffect(() => {
    const el = photoRef.current
    if (!el) return
    const onError = (e: Event) => {
      if ((e.target as HTMLElement | null)?.tagName === 'IMG') setSawImageError(true)
    }
    el.addEventListener('error', onError, true)
    return () => el.removeEventListener('error', onError, true)
  }, [])

  const { isDead } = useDeadListingDetection({
    listingId: listing.id,
    imageUrls,
    mode: 'probe',
    enabled: sawImageError && !isHidden,
  })

  // Outside-mousedown closes the "all features" popover.
  React.useEffect(() => {
    if (!tagsOpen) return
    const h = (e: MouseEvent) => {
      if (tagsRef.current && !tagsRef.current.contains(e.target as Node)) setTagsOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [tagsOpen])

  const handleHide = () => {
    hideListing(listing.id)
  }

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (listing.images.length > 0) {
      openGallery(listing.id, currentImageIndex)
    }
  }

  const handleExternalLink = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.open(api.getRedirectUrl(listing.id), '_blank', 'noopener,noreferrer')
  }

  const handleViewDetails = () => {
    window.open(`/listings/${listing.id}`, '_blank', 'noopener,noreferrer')
  }

  // Location line → Google Maps. Prefer exact coordinates; fall back to the
  // written address so listings without a geo point still resolve. This is a
  // plain maps.google.com URL — no API key involved, so it works regardless of
  // the Maps JS/Geocoding key state.
  const mapsUrl = React.useMemo(() => {
    const q =
      listing.location && Number.isFinite(listing.location.lat) && Number.isFinite(listing.location.lon)
        ? `${listing.location.lat},${listing.location.lon}`
        : [listing.street, listing.district, listing.city, 'Czechia'].filter(Boolean).join(', ')
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
  }, [listing.location, listing.street, listing.district, listing.city])

  const handleLocationClick = (e: React.MouseEvent) => {
    e.stopPropagation() // the card itself opens the detail page
    window.open(mapsUrl, '_blank', 'noopener,noreferrer')
  }

  const handleKeywordClick = React.useCallback((keywordId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation() // Prevent card click/navigation
    addTag(keywordId)
    window.dispatchEvent(new CustomEvent('tag-added'))

    // Navigate to search page if not already there
    if (!window.location.pathname.startsWith('/search')) {
      router.push('/search')
    }
  }, [addTag, router])

  // Build amenities list dynamically from keywords_cz (via store)
  const amenities = useListingAmenities(listing)

  // Sort: highlighted/active filter tags bubble to the front
  const sortedAmenities = React.useMemo(() => {
    if (!highlightedKeywords.length) return amenities
    return [...amenities].sort((a, b) => {
      const aH = highlightedKeywords.includes(a.key)
      const bH = highlightedKeywords.includes(b.key)
      return aH === bH ? 0 : aH ? -1 : 1
    })
  }, [amenities, highlightedKeywords])

  // The number shown matches production: estimated total (base + recurring
  // extras) unless the use_total_price setting is explicitly off.
  const displayPrice = effectiveListingPrice(listing, filters.use_total_price)

  // Early return for hidden listings - must be after all hooks
  if (isHidden) {
    return null
  }

  const { rooms, area, floor } = listing
  const update = listing.updates && listing.updates.length > 0 ? listing.updates[0] : null

  const renderAmenityChip = ({ key, label, icon: Icon }: AmenityItem) => {
    // Highlight anything the current search is matching on, not just the tags
    // the user clicked: `highlightedKeywords` is (parsed query keywords OR
    // active tags) ∪ Settings must-haves (search page ~L947-961). Must-haves
    // never enter `activeTags` — they're merged straight into the request —
    // so keying the highlight off activeTags alone left them sorted to the
    // front of the row but rendered plain. Text-search keywords benefit too.
    const isMustHave = mustHaveKeys.has(key)
    const highlighted = activeTags.includes(key) || highlightedKeywords.includes(key)
    return (
      <Chip
        key={key}
        small
        label={label}
        icon={<Icon size={12} className="shrink-0" />}
        active={highlighted}
        onClick={(e) => handleKeywordClick(key, e)}
        title={
          isMustHave
            ? t('listingCard.mustHaveMatch', 'Must-have — matched (edit in Settings)')
            : `${t('listingCard.filterBy', 'Filter by')} ${label}`
        }
      />
    )
  }

  return (
    <article
      onClick={handleViewDetails}
      // data-listing-card is CARD_ATTR in lib/use-grid-keyboard-nav.ts — the grid
      // finds its keyboard stops (and their ids) by querying for it, so nothing
      // needs registering and progressive rendering is handled for free.
      data-listing-card={listing.id}
      tabIndex={tabIndex}
      className={cn(
        'relative flex h-full cursor-pointer flex-col overflow-hidden rounded-shape-lg border bg-surface',
        'transition-[box-shadow,transform,border-color] duration-200 ease-standard hover:-translate-y-0.5 hover:shadow-e2',
        'focus:outline-none',
        isSaved ? 'border-primary/40' : 'border-outline-variant',
        focused && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
        isDead && 'opacity-75',
        className
      )}
      style={isDead ? { order: 1 } : undefined}
    >
      {/* Photo carousel with design chrome (hover arrows, counter, dots) */}
      <div ref={photoRef} onClick={handleImageClick}>
        <RealPhotos
          images={imageUrls}
          alt={listing.title}
          palette={paletteIndex}
          aspect={isCompact ? '16 / 9' : '4 / 3'}
          onIndexChange={setCurrentImageIndex}
          overlay={
            <>
              <div
                className={cn(
                  'absolute top-[10px] flex gap-1.5',
                  variant === 'map' ? 'right-[42px]' : 'right-[10px]'
                )}
              >
                {/* data-kbd marks the control the X / L keys click. `contents`
                    so the wrapper adds no box to the flex row. */}
                <span data-kbd="hide" style={{ display: 'contents' }}>
                  <ScrimBtn icon={EyeOff} title={t('listingCard.hideListing', 'Hide this listing')} onClick={handleHide} />
                </span>
                <span data-kbd="like" style={{ display: 'contents' }}>
                  <SaveScrimBtn listingId={listing.id} />
                </span>
              </div>
              {isDead && (
                <div className="absolute inset-x-0 bottom-0 bg-[rgba(26,23,23,0.6)] px-3 py-2 backdrop-blur-sm">
                  <p className="text-center text-xs text-white/90">
                    {t('listingCard.unavailable', 'May no longer be available')}
                  </p>
                </div>
              )}
            </>
          }
        />
      </div>

      {/* Body */}
      <div className={cn('flex flex-1 flex-col', isCompact ? 'p-[10px_12px_12px]' : 'p-[12px_14px_14px]')}>
        {/* Title + AI summary button (design cards.jsx:298-322) */}
        <div className="flex items-center gap-2">
          <div
            onClick={(e) => {
              e.stopPropagation()
              handleViewDetails()
            }}
            title={t('listingCard.openDetails', 'Open listing details')}
            className={cn(
              'min-w-0 flex-1 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap font-bold leading-[1.35] tracking-[-0.2px]',
              isCompact ? 'text-[14.5px]' : 'text-[16.5px]'
            )}
          >
            {listing.title}
          </div>
          {listing.description ? (
            <span data-kbd="summary" style={{ display: 'contents' }}>
              <AiSummaryButton listing={listing} variant="icon" />
            </span>
          ) : null}
        </div>

        {/* Location → opens Google Maps at the listing's point (w-fit so only
            the text itself is the link, not the rest of the row). */}
        {(listing.district || listing.city) && (
          <button
            type="button"
            onClick={handleLocationClick}
            title={t('listingCard.openInMaps', 'Show on Google Maps')}
            aria-label={t('listingCard.openInMaps', 'Show on Google Maps')}
            className="mt-[3px] flex w-fit max-w-full min-w-0 items-center gap-1 rounded text-left transition-colors hover:text-primary"
          >
            <MapPin size={14} className="shrink-0 text-ink-muted" />
            <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] text-ink-muted transition-colors hover:text-primary">
              {[listing.district, listing.city].filter(Boolean).join(', ')}
            </span>
          </button>
        )}

        {/* Live-update strip (render-if-present) */}
        {update && (
          <div
            title={t('listingCard.liveUpdateTip', 'Live update from the owner — posted without changing the listing.')}
            className="mt-[10px] flex items-start gap-[7px] rounded-shape-sm bg-primary-container px-[11px] py-[7px] text-on-primary-container"
          >
            <Megaphone size={14} className="mt-[1.5px] shrink-0" />
            <span className="min-w-0 flex-1 text-[12px] font-medium leading-[1.45]">{update.text}</span>
            <span className="mt-[2px] whitespace-nowrap font-mono text-[9.5px] opacity-70">{update.when}</span>
          </div>
        )}

        {/* Price block with breakdown popover + position bar */}
        <CardPriceBlock listing={listing} displayPrice={displayPrice} stats={stats} isCompact={isCompact} />

        {/* Rooms / area / floor band — cells render only for present values */}
        <StatRow
          listing={{ rooms, area, floor, totalFloors: listing.total_floors }}
          size={isCompact ? 12.5 : 15}
        />

        {/* Keyword chips — active filter tags float to the front */}
        {sortedAmenities.length > 0 && (
          <div ref={tagsRef} className="relative mt-[10px] flex flex-wrap gap-1.5">
            {sortedAmenities.slice(0, 10).map(renderAmenityChip)}
            {sortedAmenities.length > 10 && (
              <Chip
                small
                dashed
                label={`+${sortedAmenities.length - 10}`}
                active={tagsOpen}
                onClick={() => setTagsOpen((v) => !v)}
                title={t('listingCard.showAllFeatures', 'Show all features')}
              />
            )}
            {tagsOpen && (
              <div className="absolute inset-x-0 bottom-[calc(100%+6px)] z-[60] animate-pop-in rounded-shape-md border border-outline bg-surface p-3 shadow-e3">
                <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.7px] text-ink-muted">
                  {t('listingCard.allFeatures', 'All features')} ({sortedAmenities.length})
                </div>
                <div className="flex flex-wrap gap-1.5">{sortedAmenities.map(renderAmenityChip)}</div>
              </div>
            )}
          </div>
        )}

        {/* Footer: source (left) + details link (right) */}
        <div className="mt-auto flex items-center justify-between gap-2 pt-3">
          {listing.marketplace === 'Roostr' ? (
            <span
              title={t(
                'listingCard.postedOnRoostrTip',
                'Posted directly on Roostr by the owner — ranked above imported listings in search'
              )}
              className="inline-flex min-w-0 items-center gap-[5px] text-[12.5px] font-semibold text-ink-secondary"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/logo.png" alt="" className="block h-[14px] w-auto" />
              <span className="truncate">{t('listingCard.postedOnRoostr', 'Posted on Roostr')}</span>
            </span>
          ) : (
            <button
              type="button"
              onClick={handleExternalLink}
              title={`${t('listingCard.sourceTip', 'Original listing on')} ${listing.marketplace || ''}`}
              className="inline-flex min-w-0 items-center gap-1 text-[12.5px] font-semibold text-ink-secondary transition-colors hover:text-ink"
            >
              <span className="truncate">{listing.marketplace || t('listingCard.viewSource', 'View source')}</span>
              <ExternalLink size={13} className="shrink-0" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleViewDetails()
            }}
            title={t('listingCard.openDetails', 'Open listing details')}
            className="inline-flex shrink-0 items-center gap-[3px] text-[12.5px] font-semibold text-primary"
          >
            {t('listingCard.details', 'Details')}
            <ArrowUpRight size={13} />
          </button>
        </div>
      </div>
    </article>
  )
})
